import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { HttpClient } from '@angular/common/http';
import { environment } from 'src/environments/environment';
import { first, map } from 'rxjs/operators';
import { SourceOfResource } from 'src/app/domain/models/resources/source-of-resource-enum';
import { AlertService } from '../alert-service/alert.service';
import { LoadingType } from 'src/app/domain/models/alert/loading-type-enum';
import { FunnelFlow } from 'src/app/domain/models/funnel/register-funnels/funnel-flow-register';
import { AlertPosition, AlertType } from 'src/app/domain/models/alert/alert-type-enum';
import { IBINRule, IBINRuleCriteria, IBINRules } from 'src/app/domain/models/credit-card/creditcard-bin-rules';
import { BINRuleCriteria } from 'src/app/domain/models/credit-card/bin-rule-criteria-enum';
import { BINRule } from 'src/app/domain/models/credit-card/bin-rules-enum';
import { ServicesEnum } from 'src/app/domain/models/services.enum';

@Injectable({
  providedIn: 'root'
})
export class BINRulesService {
  public binRules!: Array<IBINRules>;
  
  constructor(private http: HttpClient, private alertService: AlertService) { }

  public validateCreditCardBIN(BIN: string, funnelFlow: FunnelFlow) {
    let binRules = this.binRules?.find(binRule => binRule?.binSet?.includes(BIN) && binRule?.applyRuleOn?.includes(ServicesEnum[ServicesEnum.Registration]));
    let allowedBIN = true, allowedForLastAttempt = true;
    if(binRules?.isActive!) {
      // console.log(binRules);
      allowedBIN = (binRules.defaultRule == BINRule[BINRule.AllowForAll])? true : false;
      allowedForLastAttempt = binRules.allowedByDefaultForLastAttempt!;
      // console.log("default rule:", binRules.defaultRule);
      binRules.rules.forEach((binRule: IBINRule) => {
        // console.log("rule:", binRule.rule);
        let criteriaMet = 0;
        binRule.criteria.forEach((criteria: IBINRuleCriteria) => {
          let criteriaValuesList = criteria.value.split(",").map(criteriaValue => criteriaValue.trim());
          switch(criteria.criteria) {
            case BINRuleCriteria[BINRuleCriteria.Offer]:
              (criteriaValuesList.includes(funnelFlow.SubscriptionProduct?.funnel?.offerId!))? criteriaMet++ : 0;
              break;
            case BINRuleCriteria[BINRuleCriteria.Affiliate]:
              (criteriaValuesList.includes(funnelFlow.EverflowAffiliate?.everflowAffiliateId!))? criteriaMet++ : 0;
              break;
            case BINRuleCriteria[BINRuleCriteria.Sub1]:
              (criteriaValuesList.includes(funnelFlow.UserEverflowEvent?.EverflowSub1!))? criteriaMet++ : 0;
              break;
            case BINRuleCriteria[BINRuleCriteria.Sub2]:
              (criteriaValuesList.includes(funnelFlow.UserEverflowEvent?.EverflowSub2!))? criteriaMet++ : 0;
              break;
            case BINRuleCriteria[BINRuleCriteria.Sub3]:
              (criteriaValuesList.includes(funnelFlow.UserEverflowEvent?.EverflowSub3!))? criteriaMet++ : 0;
              break;
          }
        });
        // console.log("count of rule criteria: ", binRule.criteria?.length, " - Count of critera met: ", criteriaMet);
        if(binRule.criteria?.length == criteriaMet) {
          allowedBIN = (binRule.rule == BINRule[BINRule.AllowByCriteria])? true : false;
          allowedForLastAttempt = binRule?.allowedForLastAttempt ?? true;
          // console.log("all criteria met" + " -> ¿Allow BIN?: ", allowedBIN);
          for(let exception of binRule.exceptions) {
            let exceptionValuesList = exception.value.split(",").map(exceptionValue => exceptionValue.trim());
            let exceptionIsMet = false;
            switch(exception.criteria) {
              case BINRuleCriteria[BINRuleCriteria.Offer]:
                exceptionIsMet = exceptionValuesList.includes(funnelFlow.SubscriptionProduct?.funnel?.offerId!);
                break;
              case BINRuleCriteria[BINRuleCriteria.Affiliate]:
                exceptionIsMet = exceptionValuesList.includes(funnelFlow.EverflowAffiliate?.everflowAffiliateId!);
                break;
              case BINRuleCriteria[BINRuleCriteria.Sub1]:
                exceptionIsMet = exceptionValuesList.includes(funnelFlow.UserEverflowEvent?.EverflowSub1!);
                break;
              case BINRuleCriteria[BINRuleCriteria.Sub2]:
                exceptionIsMet = exceptionValuesList.includes(funnelFlow.UserEverflowEvent?.EverflowSub2!);
                break;
              case BINRuleCriteria[BINRuleCriteria.Sub3]:
                exceptionIsMet = exceptionValuesList.includes(funnelFlow.UserEverflowEvent?.EverflowSub3!);
                break;
            }
            if(exceptionIsMet) {
              allowedForLastAttempt = binRules?.allowedByDefaultForLastAttempt ?? true;
              allowedBIN = !allowedBIN;
              // console.log("an exception is met -> ¿Allow BIN?: ", allowedBIN);
              break;
            } else {
              // console.log("no exception is met -> ¿Allow BIN?: ", allowedBIN);
            }
          }
        }
      });
    }
    // console.log({allowedBIN});
    // console.log({allowedForLastAttempt});
    return {allowedBIN: allowedBIN, allowedForLastAttempt: allowedForLastAttempt };
  }

  public initBINRulesList(sourceOfResource = SourceOfResource.AdminAPI) {
    this.alertService.setLoading(true, LoadingType.Default, ['Loading...']);
    this.requestBINRulesByBIN(sourceOfResource).pipe(first()).subscribe({
      next: (binRules: Array<IBINRules>) => {
        this.alertService.setLoading(false);
        return binRules;
      },
      error: (error: any) => {
        if(sourceOfResource == SourceOfResource.AdminAPI) {
          this.initBINRulesList(SourceOfResource.S3);
        } else {
          this.alertService.showAlert({
            Description: error,
            Position: AlertPosition.BottomRight,
            Type: AlertType.Error,
          });
        }
      },
    });
  }

  private requestBINRulesByBIN(sourceOfResource: SourceOfResource): Observable<Array<IBINRules>> {
    switch(sourceOfResource) {
      case SourceOfResource.S3:
        return this.getBrandsResourcesFromS3().pipe(map((binRulesListResponse: Array<IBINRules>) => {
          this.binRules = binRulesListResponse;
          return binRulesListResponse;
        }));
      default:
        return this.getBrandsResourcesFromAdminAPI().pipe(map((binRulesListResponse: Array<IBINRules>) => {
          this.binRules = binRulesListResponse;
          return binRulesListResponse;
        }));
    }
  }

  private getBrandsResourcesFromAdminAPI(): Observable<Array<IBINRules>> {
    return this.http.get<{ data: Array<IBINRules> }>(`${environment.apiNodeUrl}/creditcard-bin-rules`).pipe(map((brandsResourcesResponse: { data: Array<IBINRules> }) => {
      return brandsResourcesResponse?.data;
    }));
  }

  private getBrandsResourcesFromS3(): Observable<Array<IBINRules>> {
    return this.http.get<Array<IBINRules>>(`${environment.s3BINRules}`).pipe(map((brandsResourcesResponse: Array<IBINRules>) => {
      return brandsResourcesResponse;
    }));
  }
}